import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CreateAssistant from './pages/CreateAssistant';
import AssistantChat from './pages/AssistantChat';
import AuthCallback from './pages/AuthCallback';
import GoogleAnalytics from './components/GoogleAnalytics';
import { isTestMode } from './services/supabase';
import { ROUTES } from './constants';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to={ROUTES.LANDING} replace />;
};

// Public Route Component (redirects to dashboard if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return user ? <Navigate to={ROUTES.DASHBOARD} replace /> : <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Router>
      <GoogleAnalytics />
      <Routes>
        {/* Auth callback route - must be first */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Public routes */}
        <Route path={ROUTES.LANDING} element={<Landing />} />
        <Route
          path={ROUTES.LOGIN}
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Public assistant chat - must come before specific routes to avoid conflicts */}
        <Route path={ROUTES.ASSISTANT_CHAT} element={<AssistantChat />} />

        {/* Protected routes */}
        <Route
          path={ROUTES.DASHBOARD}
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CREATE_ASSISTANT}
          element={
            <ProtectedRoute>
              <CreateAssistant />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.EDIT_ASSISTANT}
          element={
            <ProtectedRoute>
              <CreateAssistant />
            </ProtectedRoute>
          }
        />

        {/* Default redirect for unmatched routes */}
        <Route path="*" element={<Navigate to={ROUTES.LANDING} replace />} />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      {isTestMode && (
        <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-black text-center py-2 z-50">
          🧪 Test Mode - No API keys required. Add your API keys to .env.local for full functionality.
        </div>
      )}
      <AppRoutes />
    </AuthProvider>
  );
};

export default App;
